// ADR 29.9.26al §3 — a page's width is a property of its route.
//
// A leaf declares `handle.frame` beside its `titleKey`; `ShellBody` draws the
// outlet's `PageFrame` at that width. Absent means `prose`, which is the
// shell's cap from before this ADR, so an unmigrated page is unchanged.
//
// **Leaf only**, as `useRouteBackTo` reads `backTo`: a pathless guard carrying
// `frame` would otherwise widen every page under it, invisibly from the leaf's
// own line. A value outside the four words is `prose` at runtime — `handle`
// is `any`, so a typo compiles — and 29.9.26al AC17 fails it in the suite.

import { useMatches } from 'react-router-dom'

import type { PageWidth } from './PageFrame'

/** What a leaf route may add beside `titleKey`. Absent means `prose`. */
export interface RouteFrameHandle {
  frame?: PageWidth
}

const WIDTHS: ReadonlySet<unknown> = new Set<PageWidth>(['form', 'prose', 'wide', 'full'])

/** Whether `value` is one of the four frame words. */
function isPageWidth(value: unknown): value is PageWidth {
  return WIDTHS.has(value)
}

/** The leaf match's `handle.frame`; `prose` if it is absent or not one of the four words. */
export function useRouteFrame(): PageWidth {
  const matches = useMatches()
  const handle = matches[matches.length - 1]?.handle
  if (typeof handle !== 'object' || handle === null) return 'prose'
  const frame = (handle as RouteFrameHandle).frame
  return isPageWidth(frame) ? frame : 'prose'
}
